// Shared by Cloud Functions and the frontend (imported there as @shared).
// When a league's games play by themselves.
//
// league.automation: {enabled, times: ["HH:MM", ...], timeZone, lastSlot}
// Each time is a "slot": one game day is played at each slot, in the
// league's time zone. lastSlot ("YYYY-MM-DDTHH:MM", local) is the last slot
// that was played, so a missed slot is caught up at the next check and
// never played twice.

export const MAX_TIMES = 4;

/**
 * Default schedule for a new league: college plays one game day each
 * evening; pro plays two (afternoon and evening), matching the preset's
 * gameDaysPerRealDay.
 * @param {string} type "college" or "pro"
 * @param {string} [timeZone]
 * @return {object} automation settings, off until an admin turns them on
 */
export function defaultAutomation(type, timeZone = "America/Chicago") {
  return {
    enabled: false,
    times: type === "pro" ? ["13:00", "20:00"] : ["20:00"],
    timeZone,
    lastSlot: null,
  };
}

/**
 * The schedule the offseason follows: one step a day, at the day's last
 * game time.
 * @param {object} automation
 * @return {object} automation with only the last time
 */
export function offseasonAutomation(automation) {
  const times = [...(automation?.times ?? [])].sort();
  return {...automation, times: times.slice(-1)};
}

/**
 * The wall-clock date and time at `date` in a time zone.
 * @param {Date} date
 * @param {string} timeZone IANA name, e.g. "America/Chicago"
 * @return {object} {date: "YYYY-MM-DD", time: "HH:MM", key}
 */
export function localParts(date, timeZone) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date).map((p) => [p.type, p.value]));
  const day = `${parts.year}-${parts.month}-${parts.day}`;
  const time = `${parts.hour}:${parts.minute}`;
  return {date: day, time, key: `${day}T${time}`};
}

/**
 * The most recent slot at or before `now`, as "YYYY-MM-DDTHH:MM" local.
 * @param {object} automation
 * @param {Date} now
 * @return {string|null}
 */
export function latestSlot(automation, now) {
  const times = [...(automation.times ?? [])].sort();
  if (!times.length) return null;
  const today = localParts(now, automation.timeZone);
  const passed = times.filter((t) => t <= today.time);
  if (passed.length) return `${today.date}T${passed.at(-1)}`;
  const yesterday = localParts(new Date(now.getTime() - 24 * 3600 * 1000),
      automation.timeZone);
  return `${yesterday.date}T${times.at(-1)}`;
}

/**
 * Whether a game day should be played now: automation is on and a slot has
 * passed since the last one played.
 * @param {object} automation
 * @param {Date} now
 * @return {boolean}
 */
export function isDue(automation, now) {
  if (!automation?.enabled) return false;
  const slot = latestSlot(automation, now);
  return Boolean(slot) && slot > (automation.lastSlot ?? "");
}

/**
 * When the next slot happens, as a real instant (for showing "next games
 * at ..." in the viewer's own time zone).
 * @param {object} automation
 * @param {Date} now
 * @return {Date|null}
 */
export function nextSlotTime(automation, now) {
  const times = [...(automation?.times ?? [])].sort();
  if (!automation?.enabled || !times.length) return null;
  for (let addDays = 0; addDays <= 2; addDays++) {
    const day = localParts(new Date(now.getTime() + addDays * 24 * 3600 * 1000),
        automation.timeZone).date;
    for (const time of times) {
      const at = zonedTime(day, time, automation.timeZone);
      if (at > now) return at;
    }
  }
  return null;
}

/**
 * The instant when the wall clock in `timeZone` shows `day` `time`.
 * @param {string} day "YYYY-MM-DD"
 * @param {string} time "HH:MM"
 * @param {string} timeZone
 * @return {Date}
 */
export function zonedTime(day, time, timeZone) {
  const [y, m, d] = day.split("-").map(Number);
  const [h, min] = time.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, h, min);
  // Offset of the zone at that moment: what its clock shows minus UTC.
  const shown = localParts(new Date(guess), timeZone);
  const [sy, sm, sd] = shown.date.split("-").map(Number);
  const [sh, smin] = shown.time.split(":").map(Number);
  const offset = Date.UTC(sy, sm - 1, sd, sh, smin) - guess;
  return new Date(guess - offset);
}

/**
 * @param {object} automation {times, timeZone}
 * @return {string[]} problems; empty when valid
 */
export function validateAutomation(automation) {
  const errors = [];
  const times = automation?.times;
  if (!Array.isArray(times) || times.length < 1 || times.length > MAX_TIMES) {
    errors.push(`Choose 1 to ${MAX_TIMES} game times.`);
  } else {
    if (times.some((t) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(t))) {
      errors.push("Game times must look like 20:00.");
    }
    if (new Set(times).size !== times.length) {
      errors.push("Each game time can only be listed once.");
    }
  }
  try {
    new Intl.DateTimeFormat("en-US", {timeZone: automation?.timeZone});
    if (!automation?.timeZone) errors.push("Choose a time zone.");
  } catch {
    errors.push("That time zone isn't recognized.");
  }
  return errors;
}
